/**
 * Loads a script
 *
 * @param src url of the script to load
 * @param onload callback function after the script has loaded
 */
const loadScript = (src, onload) => {
  const newScript = document.createElement('script');
  newScript.type = 'text/javascript';
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode.insertBefore(newScript, firstScript);

  if (onload) {
    newScript.onload = onload;
  }

  newScript.src = src;
};

/**
 * Loads organic terms from wpDatastack and into a provided class
 *
 * @param targetClass classname of the container element (block)
 */
const loadOrganicTerms = (targetClass) => {
  const { blockInjector, wpDataStack } = window?.s1 ?? {};

  const { ruleid, sid, ttag } = blockInjector ?? {};
  const { campaignDomain, organicAFD, organicTerms } = wpDataStack?.pageInfo?.organicInfo ?? {};

  let linkMarkup = '';
  const organicCta = document.querySelector(`.${targetClass}`);

  // If there are organic terms and it's not the paginated fluid template,
  // build the standard CTA markup.
  if (
    organicAFD
    && Object.keys(organicTerms ?? {}).length > 0
    && Object.keys(campaignDomain).length > 0
    && wpDataStack?.pageType?.['s1-page-type']?.name !== 'multi-fluid'
  ) {
    const organicHref = new URL(`http://${campaignDomain}`);
    const { searchParams } = organicHref;

    searchParams.set('campaign', 'organic');
    searchParams.set('ttag', ttag);
    searchParams.set('sessionid', sid);
    searchParams.set('rid', ruleid);

    Object.keys(organicTerms ?? {}).forEach((key, index) => {
      const linkTerm = encodeURIComponent(organicTerms[key]).replace(/%20/g, '+');
      const href = `${organicHref.toString()}&query=${linkTerm}&compkey=${linkTerm}`;

      linkMarkup += `
        <a
          class="cta-organic cta-block__item c${index + 1}"
          data-cltr=""
          data-linkpos="${index + 1}"
          href="${href}"
          rel="nofollow"
          style="user-select: none;"
          target="_blank"
        >
          <span class="cta-block__counter"></span>
          <span class="cta-block__image"></span>
          <span class="cta-block__col">
            <span class="cta-block__description">${organicTerms[key]}</span>
            <span class="cta-block__button"></span>
          </span>
          <span class="cta-block__icon"></span>
          <span class="cta-block__secondary-label"></span>
        </a>
      `;
    });

    // container to append to
    const ctaBlock = document.createElement('div');

    ctaBlock.className = 'cta-block__grid';
    ctaBlock.innerHTML = linkMarkup;

    organicCta.appendChild(ctaBlock);
  } else {
    // Hide the CTA if no organic terms exist
    organicCta.style.display = 'none';
  }
};

/**
 * checks if GA is loaded before using function to be executed (usually read-aloud-player)
 *
 * @param callback function to execute when GA has loaded
 * @param counter (int) number of times to run the check before firing the player anyway
 */
const checkGa = (callback, counter) => {
  if (typeof window.ga === 'function' || counter <= 0) {
    callback();
  } else {
    setTimeout(() => {
      checkGa(callback, counter - 1);
    }, 200);
  }
};

/**
 * Takes in an element and return true if the latter is visible on the page or false if it isn't.
 *
 * @param element  HTML DOM element
 * @returns {boolean}
 */
const isElementVisible = (element) => {
  if (!element) return false;

  const style = element?.style?.display || window.getComputedStyle(element).display;
  return style && style !== 'none' && element.offsetParent !== null;
};

/**
 * Find sibling elements with a given class.
 *
 * @param {HTMLElement} elem
 *   Element to find siblings for.
 * @param {string} className
 *   Optional class to filter sibling by.
 *
 * @returns {HTMLElement[]}
 */
const getSiblingElements = (elem, className) => (
  Array.prototype.filter.call(elem?.parentNode?.children || [], (sibling) => {
    const hasClass = className ? sibling.classList.contains(className) : true;
    return sibling !== elem && hasClass;
  })
);

/**
 * Takes in an element and return true if the latter is in the viewport and false it isn't.
 *
 * @param element HTML DOM element
 * @returns {boolean} true if the element is in the viewport and false if it isn't.
 */
const isInViewport = (element) => {
  const rect = element.getBoundingClientRect();
  return isElementVisible(element) && (
    rect.top >= 0
    && rect.left >= 0
    && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
    && rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

/**
 * Transforms positioning of 1st CTA according to flexCTA flag
 */
const unifiedFlexCtaCalc = () => {
  // Find first CTA element, get position
  const element = document.querySelector('.flexcta .cta-block:first-of-type') || document.querySelector('.flexcta .cta-block');

  if (element) {
    element.classList.add('cta-block--sticky');

    // Maximum percentage of the viewport that the CTA can use when it sticks to the page.
    const flexibleViewportThreshold = parseFloat(getComputedStyle(element).getPropertyValue('--viewport-threshold')) || 0.65; // could reference datastack(s) as well

    // Height of the flex CTA
    const elementHeight = element.getBoundingClientRect().height;
    // height of the screen
    const screenHeight = window.innerHeight;

    // If the height of the flexCTA is greater than 45% of the viewport, add a bottom
    // attribute to force the flexCTA to only take 45% of the screen when its sticking.
    if (flexibleViewportThreshold * screenHeight < elementHeight) {
      element.style.bottom = `${flexibleViewportThreshold * screenHeight - elementHeight}px`;
    }
  }
};

/**
 * Transforms positioning of 1st RSoC according to flexCTA flag
 */
const unifiedFlexRSoCCalc = () => {
  // Find first RSoC element, get position
  const element = document.querySelector('.flexcta #gcsa-keywords-top');

  if (element) {
    element.classList.add('rsoc-block--sticky');

    // Maximum percentage of the viewport that the RSoC can use when it sticks to the page.
    const flexibleViewportThreshold = parseFloat(getComputedStyle(element).getPropertyValue('--viewport-threshold')) || 0.65; // could reference datastack(s) as well

    // Height of the flex RSoC
    const elementHeight = element.getBoundingClientRect().height;
    // height of the screen
    const screenHeight = window.innerHeight;

    // If the height of the flexCTA is greater than 45% of the viewport, add a bottom
    // attribute to force the flexCTA to only take 45% of the screen when its sticking.
    if (flexibleViewportThreshold * screenHeight < elementHeight) {
      element.style.bottom = `${flexibleViewportThreshold * screenHeight - elementHeight}px`;
    }
  }
};
/**
 * Fetches a response from the empty prebid-ads.js file to see if adblock is present,
 * if the fetch fails then adblock is present.
 *
 * @returns {boolean} true if adblock is present and false if otherwise
 */
const checkAdBlock = async () => {
  let adBlockEnabled = false;
  const localURL = '/wp-content/themes/unified/dist/js/prebid-ads.js';

  try {
    const response = await fetch(localURL);
    if (response.status === 200) {
      adBlockEnabled = false;
    }
  } catch (e) {
    adBlockEnabled = true;
  }

  return adBlockEnabled;
};

let correctionAttempt = 0; // global scope

function unifiedCalcCtaMaxSize(ctaElem) {
  const cta = ctaElem;

  // console.log("attempting calcCtaMaxSize on object");
  const ctaBlock = cta.getBoundingClientRect();

  // margins shouldnt count to CTA height
  // calculate total margin contribution to cta height
  /* const extraMargins = parseInt(window.getComputedStyle(cta).marginTop.slice(0, -2), 10)
    + parseInt(window.getComputedStyle(cta).marginBottom.slice(0, -2), 10); */

  // Target the group of links
  const ctaBlockLink = cta.querySelectorAll('.cta-block__item');

  // uncomment if top margins can be removed
  // let ctaHeight = ctaBlock.height - extraMargins;

  let ctaHeight = ctaBlock.height;
  correctionAttempt = 1;

  // -------------------
  // Apply modifications
  // -------------------
  while (ctaHeight > 0.6 * window.innerHeight && correctionAttempt < 30) {
    // console.log(`#${cta.id} height: ${ctaHeight} is ABOVE ${0.6*window.innerHeight},
    // 60% of the ${window.innerHeight} viewport `);

    switch (correctionAttempt) {
      case 1:
        // Correction 1
        // apply negative margins
        // console.log("Attempting Correction 1 - apply negative margins" );

        cta.style.marginLeft = '-10px';
        cta.style.marginRight = '-10px';
        break;

      case 2:
        // Correction 2
        // set letter-spacing to -1px
        // console.log("Attempting Correction 2 - set letter-spacing to -1px" );

        for (let i = 0; i < ctaBlockLink.length; i++) {
          // Correction 2
          // Set letter spacing to -1
          ctaBlockLink[i].style.letterSpacing = '-1px';
        }
        break;

      case 3:
        // Correction 3
        // line height to be set to font size
        // console.log("Attempting Correction 3 - align line-height to font-size" );

        for (let i = 0; i < ctaBlockLink.length; i++) {
          // identify cta description
          const ctaBlockText = ctaBlockLink[i].querySelector('.cta-block__description');

          // align line-height to font-size (appropriate minimum)
          ctaBlockText.style.lineHeight = window.getComputedStyle(ctaBlockText).fontSize;
        }
        break;

      default:
        // Correction 4+
        // link height to be reduced by 5 - last resort
        // console.log("Attempting Correction "+correctionAttempt+"
        // - CTA Block Link height will be contracted by 2px and be reassessed ");

        for (let i = 0; i < ctaBlockLink.length; i++) {
          if (ctaBlockLink[i].style.height === '') {
            // moving computed css property to inline
            ctaBlockLink[i].style.height = window.getComputedStyle(ctaBlockLink[i]).height;
          }
          // reduce by 2px
          ctaBlockLink[i].style.height = `${String(parseInt(ctaBlockLink[i].style.height.slice(0, -2), 10) - 2)}px`;
        }

        // console.log(`CTA Block Link height reduced to ${ctaBlockLink[0].style.height}`);
        break;
    }

    correctionAttempt++;
    ctaHeight = cta.getBoundingClientRect().height; // reasses size
    // console.log("CTA Height after corrections: " + ctaHeight);
  }

  // -------------------
  // Grow CTA to compliance max (60%)
  // -------------------
  correctionAttempt = 1;
  while (ctaHeight < 0.6 * window.innerHeight && correctionAttempt < 30) {
    // console.log(`#${cta.id} height: ${ctaHeight} is UNDER ${0.6*window.innerHeight},
    // 60% of the ${window.innerHeight} viewport `);

    // link height to be increased by 1
    // console.log("Attempting Correction "+correctionAttempt+"
    // - CTA Block Link height will be expanded by 1px and be reassessed ");

    for (let i = 0; i < ctaBlockLink.length; i++) {
      if (ctaBlockLink[i].style.height === '') {
        // moving computed css property to inline
        ctaBlockLink[i].style.height = window.getComputedStyle(ctaBlockLink[i]).height;
      }
      // reduce by 2px
      ctaBlockLink[i].style.height = `${String(parseInt(ctaBlockLink[i].style.height.slice(0, -2), 10) + 1)}px`;
    }

    // console.log(`CTA Block Link height increased to ${ctaBlockLink[0].style.height}`);

    correctionAttempt++;
    ctaHeight = cta.getBoundingClientRect().height; // reasses size
    // console.log("CTA Height after corrections: " + ctaHeight);
  }

  const percentageHeight = (ctaHeight / window.innerHeight) * 100;
  console.debug(`#${cta.id} height: ${ctaHeight} is at ${percentageHeight}% of the ${window.innerHeight} viewport `);
}

/**
 * Transforms sizing of CTAs based on compliance limits
 *
 */
function unifiedCalcCtaMaxSizeLauncher() {
  // call calcCtaMaxSize on flexCTA collapse (if available)
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof unifiedCalcCtaMaxSize === 'function' && correctionAttempt === 0) {
      console.log('attempting to load ctaComplianceCalc');
      const ctas = document.querySelectorAll('.cta-block');

      if (typeof ctas !== 'undefined' && Array.isArray(ctas)) {
        ctas.forEach(unifiedCalcCtaMaxSize);
      }
    }
  });
}

/**
 * Determine number of article headings and applying a counter reset to match
 */
const unifiedDetermineArticleHeadingCount = () => {
  let headingElementsLength = document.querySelectorAll('.toc__item--countdown').length;
  if (headingElementsLength > 0) {
    headingElementsLength += 1;
  }

  // Handle situations where no table of content exists but we still need to apply the countdown
  // styling (such as c2a articles)
  if (headingElementsLength < 1) {
    headingElementsLength = document.querySelectorAll('.countdown').length;
    if (headingElementsLength > 0) {
      headingElementsLength += 1;
    }
  }

  const tocList = document.querySelector('.toc__list');
  const content = document.querySelector('.content');
  if (tocList != null) {
    tocList.style.counterReset = `list-item ${headingElementsLength}`;
  }
  if (content != null) {
    content.style.counterReset = `heading ${headingElementsLength}`;
  }

  const countdownBodyElements = document.querySelectorAll('.countdown');
  const countdownTocElements = document.querySelectorAll('.toc__item--countdown');

  if (typeof countdownBodyElements !== 'undefined' && countdownBodyElements.length > 0) {
    for (let i = 0; i < countdownBodyElements.length; i++) {
      countdownBodyElements[i].style.setProperty('--countdown-hidden-background', 'var(--countdown-visible-background)');
      countdownBodyElements[i].style.setProperty('--countdown-hidden-text', 'var(--countdown-visible-text)');
    }
  }

  if (typeof countdownTocElements !== 'undefined' && countdownTocElements.length > 0) {
    for (let i = 0; i < countdownTocElements.length; i++) {
      countdownTocElements[i].style.setProperty('--countdown-toc-hidden-text', 'var(--countdown-toc-visible-text)');
    }
  }
};

/**
 * Check if current page is organic via ?imp= param.
 * Assume paid if ?imp= exists.
 *
 * @param queryString - i.e. window.location.search
 * @param isOrganicBI - value passed from window.s1.blockInjector.isOrganic
 * @return {boolean}
 */
const isOrganic = (queryString, isOrganicBI = false) => {
  let isOrganicBool = false;

  const urlParams = new URLSearchParams(queryString);

  // check blockInjector value first (pub-to-search)
  if (isOrganicBI) {
    isOrganicBool = true;
  } else if (!urlParams.get('imp')) {
    // fall back to IMP check in query string
    isOrganicBool = true;
  }

  return isOrganicBool;
};

/**
 * Sorts object keys alphabetically.
 * The values will be sorted as well if it is an array
 *
 * @param {Object} obj JS object to be sorted
 *
 * @returns {Object}
 */
const sortObject = (obj) => {
  const temp = {};

  Object.keys(obj)
    .sort()
    .forEach((key) => {
      temp[key] = obj[key];

      // sort value only if it's an array
      if (Array.isArray(obj[key])) {
        temp[key].sort();
      }
    });

  return temp;
};

/**
 * Deletes empty parameters
 *
 * @param {Object} params
 *
 * @returns void
 */
const deleteEmptySearchParams = (params) => {
  Array.from(params.keys()).forEach((key) => {
    if (!params.get(key)) {
      params.delete(key);
    }
  });
};

/**
 * Append current page query string to a link (href)
 * This function should be called on the page you want to pull the QS from.
 *
 * @param {String} currentHref
 * @param {String} desiredHref
 * @return {String}
 */
const addCurrentQsToHref = (currentHref, desiredHref) => {
  // get the QS from the current href
  const urlObj = new URL(currentHref);
  const searchParams = new URLSearchParams(urlObj.search);
  // remove all params from desiredHref in case they were passed accidentally
  // this prevents duplication of params and the possibility of '?' duplication
  const desiredUrlObj = new URL(desiredHref);
  desiredUrlObj.search = '';
  // append the QS from the currentHref to the desiredHref
  const finalHref = `${desiredUrlObj.href}?${searchParams.toString()}`;
  return finalHref;
};

/**
 * Replace a link element's href value
 *
 * @param {String} id
 * @param {String} replacementHref
 * @param {Document} domDocument
 * @return void
 */
const replaceElHrefById = (id, replacementHref, domDocument) => {
  const el = domDocument.getElementById(id);
  if (el && el.href) {
    el.href = replacementHref;
  }
};

export {
  replaceElHrefById,
  addCurrentQsToHref,
  loadScript,
  checkGa,
  loadOrganicTerms,
  getSiblingElements,
  isElementVisible,
  isInViewport,
  unifiedFlexCtaCalc,
  unifiedFlexRSoCCalc,
  unifiedCalcCtaMaxSize,
  unifiedCalcCtaMaxSizeLauncher,
  checkAdBlock,
  unifiedDetermineArticleHeadingCount,
  isOrganic,
  sortObject,
  deleteEmptySearchParams
};
